/**
 * Turns the /marketdata-detail response into the shape MarketDataInfoPanel renders.
 *
 * <p>In plain terms: the API answers with a flat list of quote entries; this picks out the best
 * bid and offer for the top line, and pairs the remaining bids and offers into rows for the depth
 * table.
 *
 * Moved here verbatim from ActualOrderTab, which was its only caller until the List RFQ tab
 * needed the same shape for the bond details dialog. Nothing about it changed in the move - the
 * single-bond ticket imports it from here and behaves exactly as before. It lives in Utils rather
 * than in either screen so neither owns it and the two cannot drift apart.
 *
 * @param   {object} mdResponse raw response from fetchMarketDataDetail
 * @returns {{top: object, depth: object[]}|null} null when the response carries no usable data,
 *          which callers treat as "market data is not available for this security"
 */
export const normalizeMarketDataDetail = (mdResponse) => {
  if (!mdResponse?.success || !mdResponse?.data) return null;

  const updates = mdResponse.data.updates || {};
  const top = Array.isArray(updates.top) ? updates.top : [];
  const depth = Array.isArray(updates.depth) ? updates.depth : [];
  const bestBid = top.find((t) => t.mdEntryType === "BID");
  const bestOffer = top.find((t) => t.mdEntryType === "OFFER");
  const bids = depth.filter((d) => d.mdEntryType === "BID");
  const offers = depth.filter((d) => d.mdEntryType === "OFFER");
  const maxLevels = Math.max(bids.length, offers.length);
  const dealers = [];

  for (let i = 0; i < maxLevels; i++) {
    const bid = bids[i];
    const offer = offers[i];
    dealers.push({
      bidDealer: bid?.mdEntryOriginator,
      bidYield: bid?.customYield,
      bidQuantity: bid?.mdEntrySize,
      bidPrice: bid?.mdEntryPx,
      offerDealer: offer?.mdEntryOriginator,
      offerYield: offer?.customYield,
      offerQuantity: offer?.mdEntrySize,
      offerPrice: offer?.mdEntryPx,
    });
  }

  return {
    top: {
      bidDealer: bestBid?.mdEntryOriginator,
      bidYield: bestBid?.customYield,
      bidQuantity: bestBid?.mdEntrySize,
      bidPrice: bestBid?.mdEntryPx,
      offerDealer: bestOffer?.mdEntryOriginator,
      offerYield: bestOffer?.customYield,
      offerQuantity: bestOffer?.mdEntrySize,
      offerPrice: bestOffer?.mdEntryPx,
    },
    depth: dealers,
  };
};
